import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const InteractiveTools = () => {
  const [activeTab, setActiveTab] = useState('salary');
  const [salaryData, setSalaryData] = useState({
    role: '',
    experience: '',
    location: ''
  });
  const [tradeData, setTradeData] = useState({
    product: '',
    quantity: '',
    destination: ''
  });
  const [projectData, setProjectData] = useState({
    type: '',
    duration: '',
    team: ''
  });

  const roleOptions = [
    { value: 'software-engineer', label: 'Software Engineer' },
    { value: 'data-analyst', label: 'Data Analyst' },
    { value: 'project-manager', label: 'Project Manager' },
    { value: 'hr-manager', label: 'HR Manager' }
  ];

  const experienceOptions = [
    { value: '0-2', label: '0-2 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '6-10', label: '6-10 years' },
    { value: '10+', label: '10+ years' }
  ];

  const locationOptions = [
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'delhi', label: 'Delhi' },
    { value: 'pune', label: 'Pune' }
  ];

  const productOptions = [
    { value: 'electronics', label: 'Electronics' },
    { value: 'textiles', label: 'Textiles' },
    { value: 'machinery', label: 'Machinery' },
    { value: 'chemicals', label: 'Chemicals' }
  ];

  const destinationOptions = [
    { value: 'usa', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'uae', label: 'UAE' },
    { value: 'singapore', label: 'Singapore' }
  ];

  const projectTypeOptions = [
    { value: 'software', label: 'Software Development' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'integration', label: 'System Integration' }
  ];

  const durationOptions = [
    { value: '1-3', label: '1-3 months' },
    { value: '3-6', label: '3-6 months' },
    { value: '6-12', label: '6-12 months' },
    { value: '12+', label: '12+ months' }
  ];

  const teamOptions = [
    { value: '1-5', label: '1-5 members' },
    { value: '6-10', label: '6-10 members' },
    { value: '11-20', label: '11-20 members' },
    { value: '20+', label: '20+ members' }
  ];

  const tabs = [
    { id: 'salary', label: 'Salary Benchmark', icon: 'DollarSign' },
    { id: 'trade', label: 'Trade Calculator', icon: 'Calculator' },
    { id: 'project', label: 'Project Feasibility', icon: 'FileText' }
  ];

  const handleCalculate = (type) => {
    console.log(`Calculating ${type}...`);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Interactive Business Tools
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            Make informed decisions with our comprehensive calculators and assessment tools
          </p>
        </div>

        <div className="bg-card rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-border overflow-x-auto">
            <div className="flex min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-shrink-0 px-4 md:px-6 py-3 md:py-4 flex items-center gap-2 font-semibold transition-all duration-200 text-sm md:text-base ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span className="whitespace-nowrap">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8">
            {activeTab === 'salary' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <Select
                    label="Job Role"
                    options={roleOptions}
                    value={salaryData?.role}
                    onChange={(value) => setSalaryData({ ...salaryData, role: value })}
                    placeholder="Select role"
                  />
                  <Select
                    label="Experience Level"
                    options={experienceOptions}
                    value={salaryData?.experience}
                    onChange={(value) => setSalaryData({ ...salaryData, experience: value })}
                    placeholder="Select experience"
                  />
                  <Select
                    label="Location"
                    options={locationOptions}
                    value={salaryData?.location}
                    onChange={(value) => setSalaryData({ ...salaryData, location: value })}
                    placeholder="Select location"
                  />
                </div>
                <Button
                  variant="default"
                  fullWidth
                  iconName="TrendingUp"
                  iconPosition="left"
                  onClick={() => handleCalculate('salary')}
                >
                  Calculate Salary Range
                </Button>
                <div className="bg-muted rounded-lg p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <Icon name="Info" size={20} className="text-primary" />
                    <span className="font-semibold text-foreground text-sm md:text-base">
                      Estimated Salary Range
                    </span>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-2">
                    ₹8,00,000 - ₹12,00,000
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Based on current market trends and 880+ placement data points
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'trade' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <Select
                    label="Product Category"
                    options={productOptions}
                    value={tradeData?.product}
                    onChange={(value) => setTradeData({ ...tradeData, product: value })}
                    placeholder="Select product"
                  />
                  <Input
                    label="Quantity (units)"
                    type="number"
                    placeholder="Enter quantity"
                    value={tradeData?.quantity}
                    onChange={(e) => setTradeData({ ...tradeData, quantity: e?.target?.value })}
                  />
                  <Select
                    label="Destination"
                    options={destinationOptions}
                    value={tradeData?.destination}
                    onChange={(value) => setTradeData({ ...tradeData, destination: value })}
                    placeholder="Select destination"
                  />
                </div>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Calculator"
                  iconPosition="left"
                  onClick={() => handleCalculate('trade')}
                >
                  Calculate Trade Costs
                </Button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold text-foreground mb-1">
                      ₹2,45,000
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground">Product Cost</div>
                  </div>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold text-foreground mb-1">
                      ₹45,000
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground">Shipping Cost</div>
                  </div>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold text-primary mb-1">
                      ₹2,90,000
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground">Total Cost</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'project' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <Select
                    label="Project Type"
                    options={projectTypeOptions}
                    value={projectData?.type}
                    onChange={(value) => setProjectData({ ...projectData, type: value })}
                    placeholder="Select type"
                  />
                  <Select
                    label="Duration"
                    options={durationOptions}
                    value={projectData?.duration}
                    onChange={(value) => setProjectData({ ...projectData, duration: value })}
                    placeholder="Select duration"
                  />
                  <Select
                    label="Team Size"
                    options={teamOptions}
                    value={projectData?.team}
                    onChange={(value) => setProjectData({ ...projectData, team: value })}
                    placeholder="Select team size"
                  />
                </div>
                <Button
                  variant="default"
                  fullWidth
                  iconName="FileText"
                  iconPosition="left"
                  onClick={() => handleCalculate('project')}
                >
                  Assess Feasibility
                </Button>
                <div className="bg-muted rounded-lg p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <span className="font-semibold text-foreground text-sm md:text-base">
                      Feasibility Score
                    </span>
                    <span className="text-2xl md:text-3xl font-bold text-success">87%</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Technical Viability</span>
                      <span className="font-semibold text-foreground">92%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Resource Availability</span>
                      <span className="font-semibold text-foreground">85%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Budget Alignment</span>
                      <span className="font-semibold text-foreground">84%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveTools;